import { type FormEvent, type JSX, useEffect, useRef, useState } from 'react';

import { schritt1, schritt2 } from '../api/anmeldung';
import type { Anmeldeergebnis, Hauskopf } from '../api/anmeldung';
import { ApiFehler } from '../api/fehler';
import { Feld } from '../komponenten/Feld';
import { Meldung, Hinweis } from '../komponenten/Meldung';
import { bewahre, hole, verwirf } from '../entwurf';

const MASKE = 'anmeldung';
/** Die Felder, die diese Maske selbst anzeigt (fuer den Auffangkasten). */
const FELDER = ['anmeldename', 'passwort', 'code'] as const;

export function Anmeldemaske({
  beiErfolg,
  beiFaktorFehlt,
  meldung,
  titelVerweis,
  haus,
}: {
  readonly beiErfolg: (ergebnis: Anmeldeergebnis) => void;
  /** SYS-05: ohne eingerichteten zweiten Faktor geht es zur Einrichtung. */
  readonly beiFaktorFehlt: (anmeldename: string, passwort: string, grund: ApiFehler) => void;
  readonly meldung?: string | undefined;
  readonly titelVerweis?: React.Ref<HTMLHeadingElement>;
  /** CRM-153: Firmierung und Kontakt aus den Geschäftsdaten; fehlt er, gilt das Bisherige. */
  readonly haus?: Hauskopf | undefined;
}): JSX.Element {
  // ERR-11: Der Anmeldename ueberlebt einen Fehlschlag und das Neuladen.
  // Das Passwort NICHT — siehe entwurf.ts.
  const [anmeldename, setzeAnmeldename] = useState(() => hole(MASKE)['anmeldename'] ?? '');
  const [passwort, setzePasswort] = useState('');
  const [code, setzeCode] = useState('');
  /** SYS-05: Voreingestellt AUS — Merken ist eine Entscheidung, kein Standard. */
  const [geraetMerken, setzeGeraetMerken] = useState(false);

  /*
   * ==========================================================================
   * ZWEI SCHRITTE (SYS-05, Auftraggeber am 03.09.2026)
   * ==========================================================================
   *
   * `vorgang === undefined` heißt: Schritt 1 — Anmeldename und Kennwort.
   * Sonst Schritt 2 — nur noch der Code.
   *
   * Bei einem gemerkten Gerät gibt es GAR KEINEN zweiten Schritt: Der Server
   * antwortet auf Schritt 1 schon mit der Sitzung, und der Anwender sieht das
   * Codefeld nie. Genau das ist der Zweck der Änderung.
   */
  const [vorgang, setzeVorgang] = useState<string | undefined>(undefined);

  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const nameFeld = useRef<HTMLInputElement>(null);
  const passwortFeld = useRef<HTMLInputElement>(null);
  const codeFeld = useRef<HTMLInputElement>(null);
  const meldungKasten = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bewahre(MASKE, { anmeldename });
  }, [anmeldename]);

  /**
   * Beim ersten Aufbau in das erste leere Feld — aber nur, wenn keine Meldung
   * dasteht.
   *
   * Kommt der Anwender MIT einer Meldung zurueck ("Ihre Sitzung ist beendet"),
   * setzt App den Fokus auf die Ueberschrift. Dann gehoert der Blick auf die
   * Erklaerung und nicht in ein Eingabefeld, das sie ueberspringt.
   */
  const ersterAufbau = useRef(true);
  useEffect(() => {
    if (!ersterAufbau.current) return;
    ersterAufbau.current = false;
    if (meldung !== undefined) return;
    if (anmeldename === '') nameFeld.current?.focus();
    else passwortFeld.current?.focus();
  }, [anmeldename, meldung]);

  /**
   * Nach einem Fehler den Fokus setzen — NACH dem Rendern.
   *
   * Frueher stand das unmittelbar im catch. Dort existiert der Meldungskasten
   * noch nicht, und aria-invalid haengt noch nicht am Feld: React uebernimmt
   * die Zustandsaenderung erst danach. Ein Fokus vor dem Rendern trifft den
   * alten Baum.
   */
  /*
   * Nach Schritt 1 gehoert der Fokus ins Codefeld — sonst muesste sich ein
   * Tastaturnutzer erst dorthin arbeiten, obwohl es das einzige Feld ist.
   */
  useEffect(() => {
    if (vorgang !== undefined) codeFeld.current?.focus();
  }, [vorgang]);

  useEffect(() => {
    if (fehler === undefined) return;

    const betroffen = fehler.verstoesse.find((v) => v.feld !== undefined)?.feld;
    if (betroffen === 'anmeldename') {
      nameFeld.current?.focus();
    } else if (betroffen === 'code') {
      codeFeld.current?.focus();
    } else if (betroffen === 'passwort' || fehler.kennung === 'anmeldung_fehlgeschlagen') {
      passwortFeld.current?.focus();
      // Nur hier markieren: Das Passwort wird ohnehin neu getippt. Bei einem
      // Fehler an einem anderen Feld waere Markieren ein Datenverlust — der
      // naechste Tastenanschlag loeschte eine richtige Eingabe (ERR-03).
      passwortFeld.current?.select();
    } else {
      // Kein Feldbezug — Kontosperre, Wartung, Verbindungsabbruch. Der Fokus
      // gehoert auf die Meldung, sonst steht ein Tastaturnutzer am
      // Seitenanfang und erfaehrt nie, was passiert ist.
      meldungKasten.current?.focus();
    }
  }, [fehler]);

  async function absenden(ereignis: FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (laeuft) return;

    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      if (vorgang === undefined) {
        const begonnen = await schritt1({ anmeldename, passwort });
        if (begonnen.art === 'angemeldet') {
          /* Ein gemerktes Geraet galt — der Code entfaellt (SYS-05). */
          verwirf(MASKE);
          beiErfolg(begonnen);
          return;
        }
        /*
         * Das Kennwort ist verbraucht und gehoert weg: Es steht nicht mehr im
         * Formular, und es soll auch nicht im Speicher liegen, waehrend der
         * Anwender sein Telefon sucht.
         */
        setzePasswort('');
        setzeVorgang(begonnen.vorgang);
        return;
      }

      const ergebnis = await schritt2({ vorgang, code, geraetMerken });
      verwirf(MASKE);
      beiErfolg(ergebnis);
    } catch (ursache) {
      const f = ursache as ApiFehler;

      /*
       * EIN FEHLER IM ZWEITEN SCHRITT FUEHRT ZURUECK AN DEN ANFANG.
       *
       * Der Vorgang ist beim Einloesen VERBRAUCHT — auch bei falschem Code.
       * Ein Formular, das danach weiter nach dem Code fragt, fragte nach
       * etwas, das nichts mehr bewirkt: Der naechste Versuch scheiterte
       * genauso, und der Anwender probierte Codes, ohne je eine Chance zu
       * haben.
       */
      if (vorgang !== undefined) {
        setzeVorgang(undefined);
        setzeCode('');
      }

      // SYS-05: Wer noch keinen zweiten Faktor hat, kommt nicht herein — aber
      // in eine Sackgasse gehoert er auch nicht. Der GRUND wandert mit, sonst
      // wechselt der Bildschirm ohne jede Erklaerung.
      if (f.kennung === 'zweiter_faktor_fehlt') {
        beiFaktorFehlt(anmeldename, passwort, f);
        return;
      }

      setzeFehler(f);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <main className="maske maske--anmeldung" aria-labelledby="anmeldung-titel">
      {/*
        CRM-153: oben zur Info, bei welchem Haus man sich anmeldet — die
        Firmierung aus den Geschäftsdaten. VOR der Überschrift, damit sie
        über ihr steht; der Fokus geht trotzdem auf die Überschrift oder ins
        erste Feld. Fehlt sie, fehlt die Zeile — kein Platzhalter.
      */}
      {(haus?.firmierung ?? null) !== null && <p className="maske__firma">{haus?.firmierung}</p>}
      <h1 id="anmeldung-titel" tabIndex={-1} ref={titelVerweis}>
        Anmeldung
      </h1>
      {/* CRM-156: keine Unterzeile mehr — die Firmierung oben sagt genug. */}

      {meldung !== undefined && <Hinweis ueberschrift={meldung} />}
      {fehler !== undefined && (
        <Meldung fehler={fehler} gezeigteFelder={FELDER} verweis={meldungKasten} />
      )}

      <form onSubmit={(e) => void absenden(e)} noValidate>
        {vorgang === undefined ? (
          <>
        <Feld
          beschriftung="Anmeldename"
          wert={anmeldename}
          aendern={setzeAnmeldename}
          pflicht
          automatik="username"
          ohneAutokorrektur
          verweis={nameFeld}
          verstoesse={fehler?.alleZumFeld('anmeldename') ?? []}
        />

        <Feld
          beschriftung="Passwort"
          art="password"
          wert={passwort}
          aendern={setzePasswort}
          pflicht
          automatik="current-password"
          verweis={passwortFeld}
          verstoesse={fehler?.alleZumFeld('passwort') ?? []}
        />

          </>
        ) : (
          <>
            {/*
              * SCHRITT 2: nur noch der Code.
              *
              * Der Anmeldename steht als TEXT da und nicht als Feld — er ist
              * entschieden. Ihn hier erneut zur Eingabe anzubieten hieße, den
              * ersten Schritt anzuzweifeln; wer sich vertippt hat, geht
              * zurück.
              */}
            <p className="hinweiszeile" role="status">
              Angemeldet als <strong>{anmeldename}</strong>. Bitte geben Sie jetzt den
              Bestätigungscode ein.
            </p>
          </>
        )}

        {vorgang !== undefined && (
          <>
        <Feld
          beschriftung="Bestätigungscode"
          wert={code}
          aendern={setzeCode}
          automatik="one-time-code"
          eingabeart="numeric"
          hinweisText="123456"
          verweis={codeFeld}
          verstoesse={fehler?.alleZumFeld('code') ?? []}
          hilfe="Sechs Ziffern aus Ihrer Authenticator-App. Statt der Ziffern können Sie auch einen Notfallcode eingeben, falls die Administration Ihnen welche ausgestellt hat."
        />

        {/*
          SYS-05: Dieses Gerät merken — auf der ZWEITEN Seite (0128).

          Er stand im ersten Formular und wurde dort gestellt, bevor
          feststand, ob es diese Seite überhaupt gibt. Gilt bereits ein
          gemerktes Gerät, antwortet Schritt 1 sofort mit der Sitzung, und der
          Haken wird ausdrücklich NICHT eingelöst — ein Gerät, das sich selbst
          erneuert, liefe nie ab. Er tat dann nichts, und niemand sagte es dem
          Anwender.

          Hierher kommt nur, wer kein geltendes Gerät vorzuzeigen hat: genau
          der, für den die Frage eine ist. Und wer gerade sechs Ziffern
          abgetippt hat, entscheidet ohnehin anders als jemand, der noch nicht
          weiß, ob er sie überhaupt braucht.

          Der Text sagt beides — was entfällt (der Code) und was NICHT
          entfällt (das Kennwort). Ohne diesen Satz liest sich das Kästchen
          wie „angemeldet bleiben", und das ist es nicht.
        */}
        <div className="feld feld--kasten">
          <input
            id="geraet-merken"
            type="checkbox"
            checked={geraetMerken}
            onChange={(e) => setzeGeraetMerken(e.target.checked)}
          />
          <label htmlFor="geraet-merken">Diesen Rechner merken</label>
          <p className="feld__hilfe">
            Dann entfällt dieser Code auf diesem Gerät <strong>bis
            Mitternacht</strong>; danach wird er wieder verlangt — auch wenn Sie sich
            kurz vorher angemeldet haben. Das{' '}
            <strong>Kennwort wird weiterhin jedes Mal verlangt</strong> — ein gemerkter
            Rechner ist kein offener Zugang. Sie können das Gerät jederzeit unter „Mein
            Konto" widerrufen.
          </p>
        </div>

          </>
        )}

        <div className="knopfreihe">
          {vorgang !== undefined && (
            <button
              className="knopf"
              type="button"
              disabled={laeuft}
              onClick={() => {
                /*
                 * Zurück heißt: von vorn. Der Vorgang bleibt unverbraucht
                 * liegen und läuft nach fünf Minuten von selbst ab — ihn hier
                 * ausdrücklich zu verwerfen wäre ein Aufruf mehr für nichts.
                 */
                setzeVorgang(undefined);
                setzeCode('');
              }}
            >
              Zurück
            </button>
          )}
          <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
            {laeuft
              ? 'Anmeldung läuft …'
              : vorgang === undefined
                ? 'Weiter'
                : 'Anmelden'}
          </button>
        </div>
      </form>

      {/*
        Ein Satz statt „Ich komme nicht herein" mit drei Fällen (CRM-61,
        Wunsch vom 05.10.2026). Was im Einzelfall gilt — Kennwort, Telefon,
        Sperre —, klärt die Administration; die Meldung bei Fehlversuch oder
        Sperre steht weiter oben im Formular.

        CRM-153: Statt „Ihre Administration" nennt der Satz die Kontaktadresse
        aus den Geschäftsdaten, als Link. Ist keine gepflegt, bleibt er wie
        bisher.
      */}
      <Kontakthinweis haus={haus} />
    </main>
  );
}

/**
 * Die Wählnummer für `tel:` — nur Ziffern und ein führendes Plus.
 *
 * Gepflegt wird die Nummer, wie man sie liest: „+49 (0) 511 123-45". Die
 * „(0)" ist im internationalen Format falsch gewählt und fällt weg, alles
 * andere außer Ziffern auch.
 */
export function waehlnummer(telefon: string): string {
  const ohneNull = telefon.replace(/\(\s*0\s*\)/g, '');
  const ziffern = ohneNull.replace(/[^\d]/g, '');
  return ohneNull.trim().startsWith('+') ? `+${ziffern}` : ziffern;
}

/** CRM-153: „Sie kommen nicht herein?" mit Kontakt aus den Geschäftsdaten. */
function Kontakthinweis({ haus }: { readonly haus: Hauskopf | undefined }): JSX.Element {
  const email = haus?.email ?? null;
  const telefon = haus?.telefon ?? null;

  if (email === null && telefon === null) {
    return <p className="hilfe">Sie kommen nicht herein? Wenden Sie sich an Ihre Administration.</p>;
  }

  const schreiben = email !== null && (
    <>
      Schreiben Sie an <a href={`mailto:${email}`}>{email}</a>
    </>
  );
  const anrufen = telefon !== null && (
    <>
      {email !== null ? ' oder rufen' : 'Rufen'} Sie an:{' '}
      <a href={`tel:${waehlnummer(telefon)}`}>{telefon}</a>
    </>
  );

  return (
    <p className="hilfe">
      Sie kommen nicht herein? {schreiben}
      {anrufen}.
    </p>
  );
}
